import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useForm } from "react-hook-form";
import useDebounce from "../../../../../utils/useDebounce";

const getDriverIdFromAmbulance = (assignedDriver) => {
  if (!assignedDriver) return "";
  if (typeof assignedDriver === "object") {
    return String(assignedDriver.id || assignedDriver.value || "");
  }
  return String(assignedDriver);
};

const MIN_TICKET_SEARCH_CHARS = 3;

const getTicketPatientId = (ticket) => {
  if (!ticket) return "";
  if (typeof ticket.patient === "object") {
    return String(ticket.patient?.id || "");
  }
  return String(ticket.patient || ticket.patient_id || "");
};

const getTicketPatientName = (ticket) => {
  if (!ticket) return "";
  return (
    ticket.patient_name_display ||
    [ticket.patient?.firstname, ticket.patient?.lastname].filter(Boolean).join(" ") ||
    ""
  );
};

const getTicketDisplayLabel = (ticket) => {
  if (!ticket) return "";
  const ticketNumber = ticket.ticket_number || ticket.id || "";
  const patientName = getTicketPatientName(ticket);
  return patientName ? `${ticketNumber} - ${patientName}` : String(ticketNumber);
};

const getTicketPatientPhone = (ticket) =>
  ticket?.patient_phone_display ||
  ticket?.patient_phone ||
  ticket?.patient?.phone ||
  "";

const AmbulancePatientPaymentForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token;
  const [drivers, setDrivers] = useState([]);
  const [ambulances, setAmbulances] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketSearchInput, setTicketSearchInput] = useState("");
  const debouncedTicketSearch = useDebounce(ticketSearchInput.trim(), 400);
  const shouldSearchTickets = debouncedTicketSearch.length >= MIN_TICKET_SEARCH_CHARS;

  // Fetch reference data
  const {
    data: ticketResults,
    isLoading: ticketsLoading,
  } = useFetchObjects(
    shouldSearchTickets ? ["ambulance-patient-ticket-search", debouncedTicketSearch] : null,
    shouldSearchTickets
      ? `tickets/?search=${encodeURIComponent(debouncedTicketSearch)}&page_size=200`
      : "",
    token,
    { enabled: shouldSearchTickets }
  );
  const {
    data: driversData,
    isLoading: driversLoading,
  } = useFetchObjects(["drivers"], "drivers/", token);
  const {
    data: ambulancesData,
    isLoading: ambulancesLoading,
  } = useFetchObjects(["ambulances"], "ambulances/", token);

  useEffect(() => {
    if (driversData) setDrivers(driversData.results || driversData);
    if (ambulancesData) setAmbulances(ambulancesData.results || ambulancesData);
  }, [driversData, ambulancesData]);

  const {
    handleAdd,
    loading,
  } = useAdd("ambulance-patient-payments", token);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      payment_date: new Date().toISOString().slice(0, 10),
      patient: "",
      patient_phone: "",
      ambulance: "",
      ambulance_code: "",
      plate_number: "",
      driver: "",
      pickup_location: "",
      destination: "",
      trip_charge: "0",
      discount: "0",
      final_amount: "0",
      paid_amount: "0",
      balance: "0",
      currency: "USD",
      payment_method: "1",
      payment_status: "Unpaid",
      received_by: "",
      description: "",
    },
  });

  const ambulance = watch("ambulance");
  const trip_charge = parseFloat(watch("trip_charge") || 0);
  const discount = parseFloat(watch("discount") || 0);
  const paid_amount = parseFloat(watch("paid_amount") || 0);
  const ticketOptions = Array.isArray(ticketResults)
    ? ticketResults
    : ticketResults?.results || [];

  // Calculate derived fields
  const final_amount = trip_charge - discount;
  const balance = final_amount - paid_amount;
  let payment_status = "Unpaid";
  if (paid_amount === 0) {
    payment_status = "Unpaid";
  } else if (balance === 0) {
    payment_status = "Paid";
  } else {
    payment_status = "Partial";
  }

  useEffect(() => {
    setValue("patient", getTicketPatientId(selectedTicket));
    setValue("patient_phone", getTicketPatientPhone(selectedTicket));
  }, [selectedTicket, setValue]);

  // Update ambulance code and plate number when ambulance changes
  useEffect(() => {
    const selectedAmbulance = ambulances.find((a) => String(a.id) === String(ambulance));
    setValue("driver", getDriverIdFromAmbulance(selectedAmbulance?.assigned_driver));
    setValue("ambulance_code", selectedAmbulance?.ambulance_code || "");
    setValue("plate_number", selectedAmbulance?.plate_number || "");
  }, [ambulance, ambulances]);

  const onSubmit = (data) => {
    // Ensure the calculated values are passed
    const formData = {
      ...data,
      final_amount: final_amount.toString(),
      balance: balance.toString(),
      payment_status: payment_status,
    };
    handleAdd(formData);
    // Reset form after success (handled by useAdd resetForm)
  };

  if (driversLoading || ambulancesLoading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">{t("common.loading")}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 3 },
        background:
          "radial-gradient(circle at top left, rgba(14, 116, 144, 0.12), transparent 28%), linear-gradient(180deg, #f5fbfd 0%, #ffffff 100%)",
      }}
    >
      <Box sx={{ maxWidth: 1320, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2.5, md: 3 },
            borderRadius: 4,
            color: "white",
            background: "linear-gradient(135deg, #0f5c73 0%, #117a93 55%, #0b6278 100%)",
            boxShadow: "0 24px 60px rgba(15, 92, 115, 0.22)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            gap={2}
          >
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.2 }}>
                {t("finance.ambulanceBilling", { defaultValue: "Ambulance Billing" })}
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                {t("finance.ambulancePatientPaymentTitle", {
                  defaultValue: "Ambulance Patient Payment",
                })}
              </Typography>
              <Typography sx={{ mt: 0.75, opacity: 0.9, maxWidth: 760 }}>
                {t("finance.patientPaymentHeroDesc", {
                  defaultValue:
                    "Record patient transport charges, auto-fill ambulance details, and keep payment tracking in one place.",
                })}
              </Typography>
            </Box>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip
                label={t("finance.patientService", { defaultValue: "Patient Service" })}
                sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "white" }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
              {t("finance.tripInformation", { defaultValue: "Trip Information" })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {t("finance.tripInformationDesc", {
                defaultValue:
                  "Start with the patient and transport details. The ambulance fields will fill automatically when you pick a vehicle.",
              })}
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <input type="hidden" {...register("patient")} />
              <input type="hidden" {...register("paid_amount")} />
              <input type="hidden" {...register("balance")} />
              <input type="hidden" {...register("currency")} />
              <input type="hidden" {...register("payment_method")} />
              <input type="hidden" {...register("payment_status")} />
              <input type="hidden" {...register("received_by")} />
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.date", { defaultValue: "Date" })}
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    {...register("payment_date")}
                    error={!!errors.payment_date}
                    helperText={errors.payment_date ? errors.payment_date.message : ""}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    fullWidth
                    options={ticketOptions}
                    value={selectedTicket}
                    loading={ticketsLoading}
                    filterOptions={(options) => options}
                    onChange={(event, newValue) => {
                      setSelectedTicket(newValue);
                      if (!newValue) {
                        setTicketSearchInput("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setTicketSearchInput(newInputValue);
                      }
                      if (reason === "clear") {
                        setTicketSearchInput("");
                        setSelectedTicket(null);
                      }
                    }}
                    getOptionLabel={(option) => getTicketDisplayLabel(option)}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    noOptionsText={
                      ticketSearchInput.trim().length < MIN_TICKET_SEARCH_CHARS
                        ? t("finance.typeTicketToSearch", {
                            defaultValue: `Type at least ${MIN_TICKET_SEARCH_CHARS} characters`,
                          })
                        : t("finance.noTicketFound", {
                            defaultValue: "No ticket found",
                          })
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Box>
                          <Typography variant="body1" fontWeight={700}>
                            {getTicketPatientName(option)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t("reception.ticketNumber", { defaultValue: "Ticket Number" })}:{" "}
                            {option.ticket_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getTicketPatientPhone(option) ||
                              t("finance.noPhone", { defaultValue: "No phone" })}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label={t("finance.patient", { defaultValue: "Patient" })}
                        placeholder={t("finance.searchPatientByTicketNumber", {
                          defaultValue: "Search by ticket number",
                        })}
                        error={!!errors.patient}
                        helperText={
                          errors.patient
                            ? errors.patient.message
                            : t("finance.chooseTransportedPatient", {
                                defaultValue: "Choose the patient being transported",
                              })
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                          endAdornment: (
                            <>
                              {ticketsLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.patientPhone", { defaultValue: "Patient Phone" })}
                    fullWidth
                    {...register("patient_phone")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label={t("finance.ambulance", { defaultValue: "Ambulance" })}
                    {...register("ambulance")}
                    defaultValue=""
                    error={!!errors.ambulance}
                    helperText={
                      errors.ambulance
                        ? errors.ambulance.message
                        : t("finance.selectAmbulanceAutofill", {
                            defaultValue: "Select an ambulance to auto-fill its code and plate",
                          })
                    }
                  >
                    <MenuItem value="">-- {t("common.select", { defaultValue: "Select" })} --</MenuItem>
                    {ambulances.map((ambulance) => (
                      <MenuItem key={ambulance.id} value={ambulance.id}>
                        {ambulance.ambulance_code} - {ambulance.plate_number}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.ambulanceCode", { defaultValue: "Ambulance Code" })}
                    fullWidth
                    {...register("ambulance_code")}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.plateNumber", { defaultValue: "Plate Number" })}
                    fullWidth
                    {...register("plate_number")}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label={t("finance.driver", { defaultValue: "Driver" })}
                    {...register("driver")}
                    defaultValue=""
                    error={!!errors.driver}
                    helperText={
                      errors.driver
                        ? errors.driver.message
                        : t("finance.optionalAssignDriver", {
                            defaultValue: "Optional: assign a driver for this trip",
                          })
                    }
                  >
                    <MenuItem value="">-- {t("common.select", { defaultValue: "Select" })} --</MenuItem>
                    {drivers.map((driver) => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.name || `${driver.firstname || ""} ${driver.lastname || ""}`.trim() || `Driver ${driver.id}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.pickupLocation", { defaultValue: "Pickup Location" })}
                    fullWidth
                    {...register("pickup_location")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.destination", { defaultValue: "Destination" })}
                    fullWidth
                    {...register("destination")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={800}>
                    {t("finance.paymentDetails", { defaultValue: "Payment Details" })}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.tripCharge", { defaultValue: "Trip Charge" })}
                    type="number"
                    fullWidth
                    inputMode="decimal"
                    {...register("trip_charge", {
                      validate: (value) => parseFloat(value) >= 0 || "Must be a positive number",
                    })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.discount", { defaultValue: "Discount" })}
                    type="number"
                    fullWidth
                    inputMode="decimal"
                    {...register("discount", {
                      validate: (value) => parseFloat(value) >= 0 || "Must be a positive number",
                    })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.finalAmount", { defaultValue: "Total Amount" })}
                    type="number"
                    fullWidth
                    inputMode="decimal"
                    InputProps={{ readOnly: true }}
                    value={final_amount.toString()}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={t("finance.description", { defaultValue: "Description" })}
                    {...register("description")}
                    fullWidth
                    multiline
                    minRows={4}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => navigate(-1)}
                      sx={{ textTransform: "none", fontWeight: 700, px: 4, py: 1.4 }}
                    >
                      {t("common.cancel", { defaultValue: "Cancel" })}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={loading}
                      sx={{ textTransform: "none", fontWeight: 800, px: 4, py: 1.4 }}
                    >
                      {t("common.save", { defaultValue: "Save" })}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AmbulancePatientPaymentForm;
